import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  ClipboardList,
  Users,
  Building2,
  Plus,
  ChevronsLeft,
  ChevronsRight,
  Package,
  Truck,
  Mail,
  Inbox,
  Receipt,
  Briefcase,
  RefreshCcw,
  Wallet,
  Calendar,
} from 'lucide-react';
import { Button, cn, TooltipProvider } from '@insolech/ui';
import { useAuth } from '@/features/auth/AuthContext';
// NotificationsPopover intentionally not rendered in the header; component file kept for easy revival.
// To restore, re-add the import from './NotificationsPopover' and drop <NotificationsPopover /> back into the header utility clusters.
import { LanguageSwitcher } from './LanguageSwitcher';
import { ProfileMenu } from './ProfileMenu';
import {
  useGuardedLinkClick,
  useGuardedNavigate,
} from './NavigationGuardContext';

/**
 * AppShell — the main layout chrome for authenticated app pages.
 *
 * Vertical nav rail on desktop, top nav on mobile.
 * Header: search, new-order CTA, notifications bell, user menu.
 *
 * Phase 4b iter round 2 — Fix C: the sidebar can be expanded to show labels
 * next to icons, or collapsed to icon-only. Default = expanded (more
 * discoverable for new users); preference is persisted in localStorage so
 * power users who collapse it stay collapsed across sessions.
 *
 * RTL: nav rail is `inline-start` (logical), so it flips correctly in Arabic.
 *      The chevron toggle uses `rtl-flip` so it always points the right way.
 */

// Using `any` here because lucide-react's prop type uses RefAttributes
// which doesn't compose with exactOptionalPropertyTypes via a plain
// ComponentType<{ className?: string }> annotation.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconComponent = React.ComponentType<any>;

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconComponent;
  adminOnly?: boolean;
}

// Patients nav entry removed per user request (033f835 follow-up): the
// surface adds complexity podiatrists don't need today. Page + route +
// migrations are kept in place so it can be re-enabled by adding the
// entry back here and the route block in src/routes/index.tsx.
const podiatristNav: NavItem[] = [
  { to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/orders', labelKey: 'nav.orders', icon: ClipboardList },
  { to: '/clinic', labelKey: 'nav.clinic', icon: Building2 },
];

// Phase 4f: Leads / Integrations / Knowledge added below the operational
// stack and above Email templates. Order is by frequency-of-use: orders →
// patients → clinics → products → providers → leads (sales pipeline) →
// integrations (ops monitoring) → knowledge (RAG curation) → email templates
// (config). This keeps daily-use links at the top.
// R20 — added Users between Patients and Clinics.
const adminNav: NavItem[] = [
  { to: '/admin', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/admin/orders', labelKey: 'nav.orders', icon: ClipboardList },
  { to: '/admin/users', labelKey: 'nav.users', icon: Users },
  { to: '/admin/clinics', labelKey: 'nav.clinics', icon: Building2 },
  { to: '/admin/products', labelKey: 'nav.products', icon: Package },
  { to: '/admin/providers', labelKey: 'nav.providers', icon: Truck },
  { to: '/admin/commissions', labelKey: 'nav.commissions', icon: Wallet },
  { to: '/admin/reorder-pricing', labelKey: 'nav.reorderPricing', icon: RefreshCcw },
  // One "Leads" entry with two tabs: Inbound (enquiries/triage) and Outbound
  // (the lead finder / prospecting campaigns). The old /admin/lead-finder nav
  // slot was folded in here; that path now redirects to /admin/leads/prospecting.
  { to: '/admin/leads', labelKey: 'nav.leads', icon: Inbox },
  // R35 — Zoho supplier-invoice processing (admin-only). Lands on the
  // Invoices page by default; the layout's sub-nav handles Vendors +
  // Settings tabs from there.
  { to: '/admin/zoho/invoices', labelKey: 'nav.zoho', icon: Receipt },
  { to: '/admin/email-templates', labelKey: 'nav.emails', icon: Mail },
  { to: '/admin/home-services', labelKey: 'nav.homeServices', icon: Calendar },
];

// A single "Home Services" pill for the home_services_manager role (a non-admin
// who tracks incoming home-service bookings). Points at the same
// /admin/home-services surface, gated by HomeServicesRoute, and is appended to
// whatever base nav the user's other roles provide.
const homeServicesEntryNav: NavItem = {
  to: '/admin/home-services',
  labelKey: 'nav.homeServices',
  icon: Calendar,
};

// Provider nav: a focused 2-link rail for /provider and /provider/orders. The
// provider portal is intentionally narrow — providers don't need access to
// patients, clinics, products, or admin tools.
const providerNav: NavItem[] = [
  { to: '/provider', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/provider/orders', labelKey: 'nav.orders', icon: ClipboardList },
];

// Single-entry "Agent" pill appended to the podiatrist nav when a user
// holds both roles (podiatrist + agent). The podiatrist surface is their
// primary workspace, so they get the full podiatrist nav plus this one
// extra pill that drops them into /agent for their introduced-clinics
// read-only view.
const agentEntryNav: NavItem = {
  to: '/agent',
  labelKey: 'nav.agent',
  icon: Briefcase,
};

const SIDEBAR_LS_KEY = 'insolech-sidebar-collapsed';

/**
 * useSidebarCollapsed — read/write the sidebar's collapsed preference.
 * Re-enabled R28 — Rafael wants click-to-collapse with persistence.
 */
function useSidebarCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_LS_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const setAndPersist = React.useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(SIDEBAR_LS_KEY, String(next));
    } catch {
      /* ignore quota / private browsing */
    }
  }, []);
  return [collapsed, setAndPersist];
}

/**
 * The wizard route. When the user enters /orders/new, we auto-collapse the
 * sidebar so the wizard has the maximum possible content width; on leaving
 * we auto-expand to restore the prior state. The user can still manually
 * toggle while inside the wizard — once they do, we stop fighting them on
 * the way out.
 */
const NEW_ORDER_PATH = '/orders/new';

/**
 * Module-scoped state for the wizard auto-collapse / restore dance.
 *
 * Why module scope, not React refs? Every routed page in the app wraps its
 * own `<AppShell>` (see e.g. NewOrderPage, OrdersListPage). That means a
 * route change unmounts the old AppShell and mounts a new one — useRef
 * values do not survive. Previously the snapshot was stored in a useRef:
 * entering the wizard worked (initial mount captured `collapsed=false` and
 * forced collapse), but exit silently failed because the AppShell that ran
 * the exit branch had a fresh ref with `null`, so the restore condition
 * never matched.
 *
 *   - `lastPath` is the pathname we observed in the last AppShell mount;
 *     used to detect entry/exit transitions across the unmount/remount.
 *   - `wizardSnapshot` is null when no restore is pending, or a boolean
 *     when we're tracking the pre-wizard `collapsed` value to restore.
 */
let lastPath: string | null = null;
let wizardSnapshot: boolean | null = null;

export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('common');
  // Guarded variants of the standard nav primitives. When the wizard
  // isn't dirty (or isn't mounted) they behave identically to navigate()
  // and a router Link; when it is, they open the Unsaved-progress modal
  // and only proceed on Discard / Save.
  const guardedNavigate = useGuardedNavigate();
  const onLogoClick = useGuardedLinkClick('/');
  const location = useLocation();
  const { isAdmin, isProvider, isPodiatrist, isAgent, isHomeServicesManager } = useAuth();
  // R28 — collapse toggle re-enabled per Rafael. Persists in localStorage.
  const [collapsed, setCollapsed] = useSidebarCollapsed();

  // R28 — sidebar widths. 72px collapsed gives the icons more breathing
  // room (was 64) so they don't look cramped against the right edge;
  // 192px expanded keeps every label legible while leaving the content
  // area maximum room.
  const sidebarWidth = collapsed ? 72 : 192;

  /**
   * Auto-collapse the sidebar while inside the New-order wizard, and restore
   * the previous state on leaving. Behavior:
   *  - Entering /orders/new: snapshot the current `collapsed` value (so we
   *    can restore on exit) and force collapsed=true.
   *  - User manually toggles inside the wizard: clear the snapshot so we
   *    don't undo their choice when they navigate away.
   *  - Leaving /orders/new: if a snapshot still exists (= user did not
   *    manually toggle inside the wizard), restore it.
   *
   * Implementation note: `lastPath` and `wizardSnapshot` are module-scoped
   * (see top of file) because each routed page renders its own AppShell,
   * so useRef values do NOT survive the route change. The module variables
   * persist across the unmount/remount so entry-then-exit sees a non-null
   * snapshot and restores correctly.
   */
  React.useEffect(() => {
    const prev = lastPath;
    const curr = location.pathname;
    const wasOnWizard = prev === NEW_ORDER_PATH;
    const isOnWizard = curr === NEW_ORDER_PATH;

    if (isOnWizard && !wasOnWizard) {
      // Entering the wizard: remember the previous state so we can
      // restore it on exit, then collapse. If we're already collapsed,
      // we still snapshot `false`-vs-`true` correctly so exit is a no-op.
      wizardSnapshot = collapsed;
      if (!collapsed) setCollapsed(true);
    } else if (!isOnWizard && wasOnWizard) {
      // Leaving the wizard: restore the snapshot if one is still pending
      // (= the user didn't manually toggle while inside).
      const snapshot = wizardSnapshot;
      wizardSnapshot = null;
      if (snapshot !== null && snapshot !== collapsed) {
        setCollapsed(snapshot);
      }
    }

    lastPath = curr;
    // We intentionally omit `collapsed` and `setCollapsed` from the deps:
    // this effect should only react to route changes. Manual toggles
    // during the wizard are handled by `handleManualToggle` below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Wrapper used by the footer toggle button. When the user manually
  // toggles while inside the wizard, drop the snapshot so we honour
  // their choice on the way out.
  const handleManualToggle = React.useCallback(() => {
    if (location.pathname === NEW_ORDER_PATH) {
      wizardSnapshot = null;
    }
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed, location.pathname]);

  /**
   * The sign-out chain (Supabase signOut, React Query cache clear, hard
   * nav to /auth) now lives in ProfileMenu, which is the only consumer
   * since both the admin sidebar footer and the non-admin topbar avatar
   * dropdown route through it.
   */

  // R36, Non-admin shell rewrite. Admin users keep the sidebar +
  // Collapse toggle exactly as before. Podiatrist / provider / agent
  // get a sidebar-less topbar layout where nav lives in the header and
  // the avatar opens a dropdown (Settings + Sign Out).
  const showSidebar = isAdmin;

  if (!showSidebar) {
    // Pick the non-admin shell's nav + dashboard route. Order matters
    // here, providers stay in the provider portal even when they hold
    // a second role (their surface is intentionally narrow), then we
    // check for the podiatrist + agent combo, then podiatrist-only,
    // then agent-only.
    const isPodiatristAgent = isPodiatrist && isAgent;
    let dashboardRoute: string;
    let navSource: NavItem[];
    if (isProvider) {
      dashboardRoute = '/provider';
      navSource = providerNav;
    } else if (isPodiatristAgent) {
      // Full podiatrist nav (Dashboard, Orders, Patients, Clinic) plus
      // an extra "Agent" pill that drops into their introduced-clinics
      // read-only surface.
      dashboardRoute = '/dashboard';
      navSource = [...podiatristNav, agentEntryNav];
    } else if (isPodiatrist) {
      dashboardRoute = '/dashboard';
      navSource = podiatristNav;
    } else if (isAgent) {
      // Agent-only users land on /agent and see only a single "Agent"
      // pill. The existing /agent/orders route still resolves directly,
      // but the consolidated tabbed /agent page (Phase 2) is the
      // intended surface for navigation.
      dashboardRoute = '/agent';
      navSource = [agentEntryNav];
    } else {
      // Fallback for signed-in users with no role assigned yet (the
      // admin has not granted them a role). Send them to /dashboard
      // where the protected route's loading + empty states handle it.
      dashboardRoute = '/dashboard';
      navSource = podiatristNav;
    }

    // Home Services manager (non-admin): append a Home Services pill to their
    // base nav so they can reach the read-only tracking surface.
    if (isHomeServicesManager) {
      navSource = [...navSource, homeServicesEntryNav];
    }

    // Translate the nav once per render.
    const nonAdminNav: { to: string; label: string }[] = navSource.map((item) => ({
      to: item.to,
      label: t(item.labelKey, { defaultValue: item.labelKey.split('.').pop() }),
    }));

    return (
      <TooltipProvider>
        <div className="h-screen max-sm:h-dvh overflow-hidden flex flex-col bg-surface-base">
          {/* Mobile (<640px): the header grows (h-auto) and the nav moves to
              its own full-width scrollable row - the old wrap-inside-h-14
              painted wrapped pills on top of the page content. Desktop
              unchanged. */}
          <header className="sticky top-0 z-30 h-14 max-sm:h-auto max-sm:min-h-14 max-sm:flex-wrap max-sm:py-1.5 border-b border-border-subtle bg-surface-elevated flex items-center justify-between gap-2 px-4 lg:px-6">
            {/* Left: logo + primary nav */}
            <div className="flex items-center gap-4 lg:gap-6 min-w-0">
              <Link
                to={dashboardRoute}
                onClick={onLogoClick}
                aria-label={t('nav.home', { defaultValue: 'Go to dashboard' })}
                className="flex items-center shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <img
                  src="/brand/logo-sidebar.jpg"
                  alt="INSOLECH"
                  className="h-9 w-auto"
                />
              </Link>
              {/* Desktop/tablet nav (>=640px): unchanged inline wrap inside
                  the left cluster. Hidden below sm - wrapping inside the
                  fixed-height header painted pills over the page content on
                  phones; the mobile row below replaces it. */}
              <nav
                className="hidden sm:flex items-center gap-0.5 sm:gap-1 flex-wrap"
                aria-label={t('nav.primary', { defaultValue: 'Primary navigation' })}
              >
                {nonAdminNav.map((item) => (
                  <TopNavLink key={item.to} to={item.to} label={item.label} />
                ))}
              </nav>
            </div>
            {/* Right: utility cluster (New Order hidden for providers
                and agent-only users, language, notifications, profile
                menu). Agent-only = agent role without a podiatrist role;
                pure agents cannot create orders. */}
            <div className="flex items-center gap-2 shrink-0">
              {!isProvider && !(isAgent && !isPodiatrist) ? (
                <Button
                  size="sm"
                  className="max-sm:h-10"
                  startIcon={<Plus className="h-4 w-4" />}
                  disabled={location.pathname === '/orders/new'}
                  onClick={() => void guardedNavigate('/orders/new')}
                >
                  {t('action_new_order', { defaultValue: 'New order' })}
                </Button>
              ) : null}
              <LanguageSwitcher />
              <div className="ms-1 ps-2 border-s border-border-subtle">
                <ProfileMenu />
              </div>
            </div>
            {/* Mobile (<640px) nav: its OWN full-width scrollable row as a
                direct header child (order-3 puts it after both clusters on
                the wrapped line). A separate render from the desktop nav
                above because the desktop one lives inside the left cluster,
                where w-full cannot escape to a new header row - dual render
                keeps the >=sm DOM byte-identical. */}
            <nav
              className="sm:hidden order-3 w-full flex items-center gap-0.5 overflow-x-auto whitespace-nowrap"
              aria-label={t('nav.primary', { defaultValue: 'Primary navigation' })}
            >
              {nonAdminNav.map((item) => (
                <TopNavLink key={item.to} to={item.to} label={item.label} />
              ))}
            </nav>
          </header>
          <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">{children}</main>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="h-screen max-sm:h-dvh overflow-hidden bg-surface-base flex">
        {/* Side rail — desktop. Width animates between 64px (collapsed) and
            240px (expanded). Sticky to the viewport so it never grows/shrinks
            with page content (Rafael's feedback 2026-05-05). On mobile
            (lg:hidden) the rail is hidden entirely — mobile drawer behavior
            is out of scope for this round. */}
        <aside
          aria-label={t('nav.primary', { defaultValue: 'Primary navigation' })}
          data-collapsed={collapsed ? 'true' : 'false'}
          style={{ width: sidebarWidth }}
          className="hidden lg:flex flex-col border-e border-border-subtle bg-surface-elevated transition-[width] duration-[250ms] ease-out overflow-hidden sticky top-0 h-screen self-start shrink-0"
        >
          {/* Brand — h-14 box; same height as the top nav so the dividers
              align across the row. */}
          <div className="h-14 flex items-center justify-center border-b border-border-subtle px-2">
            <Link
              to="/"
              onClick={onLogoClick}
              aria-label={t('nav.home', { defaultValue: 'Go to dashboard' })}
              className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {collapsed ? (
                <img
                  src="/brand/logo-collapsed.JPG"
                  alt="INSOLECH"
                  className="h-9 w-9"
                />
              ) : (
                <img
                  src="/brand/logo-sidebar.jpg"
                  alt="INSOLECH"
                  className="h-10 w-auto"
                />
              )}
            </Link>
          </div>

          {/* Nav links — provider rail is intentionally short; admin rail
              includes all back-office links; podiatrist rail covers their
              clinic surfaces. `min-h-0 overflow-y-auto` lets the nav
              scroll if the admin rail outgrows the viewport so the rows
              never get squashed nor visually clipped. */}
          <nav className="flex-1 min-h-0 overflow-y-auto py-4 flex flex-col gap-1 px-2">
            {/* This branch only renders when showSidebar === isAdmin is
                true, so the rail is always the admin nav. We keep the
                conditional shape minimal so the dependency on the role
                flag is explicit at the call site. */}
            {adminNav.map((item) => (
              <NavRailLink key={item.to} item={item} collapsed={collapsed} />
            ))}
          </nav>

          {/* Footer: collapse/expand toggle (Settings + Sign Out moved to the
              ProfileMenu avatar dropdown in the topbar, matching non-admin). */}
          <div className="py-3 px-2 flex flex-col gap-1 border-t border-border-subtle">
            <SidebarToggle collapsed={collapsed} onToggle={handleManualToggle} />
          </div>
        </aside>

        {/* Main column */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Top bar — sticky at viewport top. h-14 to match the sidebar's
              logo box so the divider lines line up across the row. */}
          <header className="sticky top-0 z-20 h-14 bg-surface-elevated border-b border-border-subtle flex items-center gap-2 px-6">
            {/* R25 — search input removed (not wired to anything; was
                placeholder UX). +New order moved into the right
                utility cluster, immediately before the language picker.
                Everything sits on the far right via ms-auto. */}
            <div className="flex items-center gap-2 ms-auto">
              {/* Providers don't create orders — hide the CTA for them. */}
              {!isProvider ? (
                <Button
                  size="sm"
                  className="max-sm:h-10"
                  startIcon={<Plus className="h-4 w-4" />}
                  // No-op when we're already on the wizard — clicking the
                  // CTA would otherwise open the Unsaved-progress modal
                  // only to land on the same route. Disabled state makes
                  // that obvious to the user.
                  disabled={location.pathname === '/orders/new'}
                  onClick={() => void guardedNavigate('/orders/new')}
                >
                  {t('action_new_order', { defaultValue: 'New order' })}
                </Button>
              ) : null}
              <LanguageSwitcher />
              <div className="ms-2 ps-2 border-s border-border-subtle">
                <ProfileMenu />
              </div>
            </div>
          </header>

          {/* Mobile/tablet (<lg) admin navigation. The nav rail above is
              `hidden lg:flex`, and below lg there was NO other route to the
              admin surfaces (an admin on a phone could only type URLs). A
              horizontally-scrollable pill row keeps every section one swipe
              away. Never renders at lg+ so the desktop layout is untouched. */}
          <nav
            aria-label={t('nav.primary', { defaultValue: 'Admin sections' })}
            className="lg:hidden sticky top-14 z-10 bg-surface-elevated border-b border-border-subtle flex items-center gap-1 overflow-x-auto whitespace-nowrap px-3 py-1.5"
          >
            {adminNav.map((item) => (
              <TopNavLink
                key={item.to}
                to={item.to}
                label={t(item.labelKey, {
                  defaultValue: item.labelKey.split('.').pop() ?? item.labelKey,
                })}
              />
            ))}
          </nav>

          {/* Page slot */}
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">{children}</div>
        </div>
      </div>
    </TooltipProvider>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sidebar — shared row layout                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Shared layout primitive for every clickable row in the sidebar.
 *
 * Plain flex row: padding-left of `px-3` (12px) pins the icon's X
 * coordinate; gap-3 separates icon from label. The icon is a fixed-size
 * `h-4 w-4 shrink-0` box so it never resizes. The label uses `sr-only`
 * when collapsed — the icon stays put because its container is `shrink-0`
 * and the flex row reads `gap-3` independently of label visibility.
 */
// shrink-0 stops the row from being squashed by flex-column siblings when
// the nav has more items than viewport height (the bug Rafael caught:
// without it, h-9 buttons shrank vertically + icons looked smaller +
// rows looked tighter in collapsed mode, while footer buttons were
// unaffected because the footer only has 2 items).
const SIDEBAR_ROW_BASE =
  'flex items-center gap-3 h-9 w-full px-3 rounded-md shrink-0 ' +
  'transition-colors duration-fast text-sm font-medium';
const SIDEBAR_ROW_IDLE =
  'text-text-secondary hover:text-text-primary hover:bg-sand-100';
const SIDEBAR_ROW_ACTIVE = 'bg-brand-50 text-brand-700';

function SidebarRowInner({
  icon,
  label,
  collapsed,
}: {
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
}) {
  return (
    <>
      <span
        aria-hidden="true"
        className="flex items-center justify-center h-4 w-4 shrink-0"
      >
        {icon}
      </span>
      <span className={cn('whitespace-nowrap', collapsed && 'sr-only')}>
        {label}
      </span>
    </>
  );
}

function SidebarToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const Icon = collapsed ? ChevronsRight : ChevronsLeft;
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      aria-expanded={!collapsed}
      data-testid="sidebar-toggle"
      className={cn(SIDEBAR_ROW_BASE, SIDEBAR_ROW_IDLE)}
      title={collapsed ? label : undefined}
    >
      <SidebarRowInner
        icon={<Icon className="h-4 w-4 rtl-flip" />}
        label={label}
        collapsed={collapsed}
      />
    </button>
  );
}


/**
 * TopNavLink, horizontal nav pill used by the non-admin topbar.
 *
 * Same guarded-click behaviour as NavRailLink (so the wizard's
 * unsaved-changes modal still fires when the user clicks Orders /
 * Patients / etc.), but with a pill rendering tuned for a horizontal
 * row instead of the vertical rail.
 */
function TopNavLink({ to, label }: { to: string; label: string }) {
  const onClick = useGuardedLinkClick(to);
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'px-2 sm:px-3 py-1.5 max-sm:py-2.5 rounded-md text-sm font-medium transition-colors duration-fast',
          isActive
            ? 'bg-brand-50 text-brand-700'
            : 'text-text-secondary hover:bg-sand-100 hover:text-text-primary',
        )
      }
    >
      {label}
    </NavLink>
  );
}

function NavRailLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { t } = useTranslation('common');
  const label = t(item.labelKey, { defaultValue: item.labelKey.split('.').pop() });
  const Icon = item.icon;
  // The guarded click is a no-op when nothing's dirty, so wiring this on
  // every sidebar link is cheap.
  const onClick = useGuardedLinkClick(item.to);
  // R28 — Tooltip wrapper removed for nav links. Radix Tooltip's asChild
  // Slot wraps NavLink and was apparently interfering with the className
  // composition + flex layout, causing the icons to look squashed +
  // mis-positioned in collapsed mode compared to the footer buttons.
  // Using the browser-native `title` attribute when collapsed gives us
  // the same hover-label affordance without the Slot mechanics.
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(SIDEBAR_ROW_BASE, isActive ? SIDEBAR_ROW_ACTIVE : SIDEBAR_ROW_IDLE)
      }
      aria-label={label}
      title={collapsed ? label : undefined}
    >
      <SidebarRowInner
        icon={<Icon className="h-4 w-4" />}
        label={label}
        collapsed={collapsed}
      />
    </NavLink>
  );
}
